import { Badge, Button, Input, Modal, Textarea } from '@/components/ui';
import type { RequisitoSysadmin } from '@/services/sysadmin.service';
import type { ActualizarRequisitoRequest, ActualizarRequisitoResponse } from '@/types/types';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface EditarRequisitoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  requisito: RequisitoSysadmin | null;
  onSave: (requisitoId: string, data: ActualizarRequisitoRequest) => Promise<ActualizarRequisitoResponse>;
}

export function EditarRequisitoModal({ isOpen, onClose, onSuccess, requisito, onSave }: EditarRequisitoModalProps) {
  const [formData, setFormData] = useState({
    capitulo: '',
    numeral: '',
    descripcion: '',
    posibleEvidencia: '',
  });

  const [validation, setValidation] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // Cargar datos del requisito cuando se abre el modal
  useEffect(() => {
    if (isOpen && requisito) {
      setFormData({
        capitulo: requisito.capitulo,
        numeral: requisito.numeral,
        descripcion: requisito.descripcion,
        posibleEvidencia: requisito.posibleEvidencia ?? '',
      });
      setValidation({});
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen, requisito]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.capitulo.trim()) errors.capitulo = 'Capitulo es requerido';
    if (!formData.numeral.trim()) errors.numeral = 'Numeral es requerido';
    if (!formData.descripcion.trim()) errors.descripcion = 'Descripcion es requerida';

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const getChangedFields = (): ActualizarRequisitoRequest | null => {
    if (!requisito) return null;

    const changes: ActualizarRequisitoRequest = {};

    if (formData.capitulo.trim() !== requisito.capitulo) {
      changes.capitulo = formData.capitulo.trim();
    }
    if (formData.numeral.trim() !== requisito.numeral) {
      changes.numeral = formData.numeral.trim();
    }
    if (formData.descripcion.trim() !== requisito.descripcion) {
      changes.descripcion = formData.descripcion.trim();
    }

    const originalEvidencia = requisito.posibleEvidencia ?? '';
    const newEvidencia = formData.posibleEvidencia.trim();
    if (newEvidencia !== originalEvidencia) {
      changes.posibleEvidencia = newEvidencia || null;
    }

    if (Object.keys(changes).length === 0) return null;
    return changes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !requisito) return;

    const changes = getChangedFields();
    if (!changes) {
      setErrorMessage('No se detectaron cambios para guardar.');
      return;
    }

    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await onSave(requisito.requisitoId, changes);
      if (response.success) {
        setSuccessMessage('Requisito actualizado exitosamente.');
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 1500);
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Error al actualizar requisito');
    } finally {
      setSaving(false);
    }
  };

  if (!requisito) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Requisito" size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Mensaje de exito */}
        {successMessage && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Mensaje de error */}
        {(errorMessage || Object.keys(validation).length > 0) && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              {errorMessage && <p>{errorMessage}</p>}
              {Object.keys(validation).length > 0 && (
                <ul className="list-disc list-inside mt-2">
                  {Object.entries(validation).map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* NTC (solo lectura) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            NTC{' '}
            <Badge variant="gray" className="ml-2">
              Solo lectura
            </Badge>
          </label>
          <Input type="text" value={requisito.ntcNombre} disabled className="opacity-60 cursor-not-allowed" />
        </div>

        {/* Capitulo */}
        <div>
          <label htmlFor="edit-capitulo" className="block text-sm font-medium text-gray-700 mb-2">
            Capitulo <span className="text-red-500">*</span>
          </label>
          <Input
            id="edit-capitulo"
            type="text"
            placeholder="ej: 4.1"
            value={formData.capitulo}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, capitulo: e.target.value }));
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.capitulo;
                return copy;
              });
            }}
            disabled={saving}
          />
        </div>

        {/* Numeral */}
        <div>
          <label htmlFor="edit-numeral" className="block text-sm font-medium text-gray-700 mb-2">
            Numeral <span className="text-red-500">*</span>
          </label>
          <Input
            id="edit-numeral"
            type="text"
            placeholder="ej: 4.1.2"
            value={formData.numeral}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, numeral: e.target.value }));
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.numeral;
                return copy;
              });
            }}
            disabled={saving}
          />
        </div>

        {/* Descripcion */}
        <div>
          <label htmlFor="edit-descripcion" className="block text-sm font-medium text-gray-700 mb-2">
            Descripcion <span className="text-red-500">*</span>
          </label>
          <Textarea
            id="edit-descripcion"
            placeholder="Describe el requisito detalladamente..."
            value={formData.descripcion}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, descripcion: e.target.value }));
              setValidation((prev) => {
                const copy = { ...prev };
                delete copy.descripcion;
                return copy;
              });
            }}
            rows={4}
            disabled={saving}
          />
        </div>

        {/* Posible Evidencia */}
        <div>
          <label htmlFor="edit-posibleEvidencia" className="block text-sm font-medium text-gray-700 mb-2">
            Posible Evidencia <span className="text-gray-500">(Opcional)</span>
          </label>
          <Input
            id="edit-posibleEvidencia"
            type="text"
            placeholder="ej: Manual de calidad, registros, certificados..."
            value={formData.posibleEvidencia}
            onChange={(e) => setFormData((prev) => ({ ...prev, posibleEvidencia: e.target.value }))}
            disabled={saving}
          />
          <p className="text-xs text-gray-400 mt-1">Dejar vacio para eliminar la evidencia sugerida.</p>
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving || successMessage !== ''}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
